import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './TransferEnvironmentsPage.css';

const SSLMODE_OPTIONS = ['prefer', 'require', 'verify-ca', 'verify-full', 'disable'];

const EMPTY_FORM = {
  name: '',
  label: '',
  db_host: '',
  db_port: 5432,
  db_name: 'cerebralmachines',
  db_user: 'admin',
  db_pass: '',
  db_sslmode: 'prefer',
  qdrant_host: '',
  qdrant_grpc_port: 6334,
};

function EnvFormModal({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...initial }));
  const isEdit = !!initial?.id;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const setNum = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: parseInt(e.target.value, 10) || 0 }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="te-modal-overlay" onClick={onCancel}>
      <div className="te-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Environment' : 'Add Environment'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="te-form-row">
            <div className="te-form-group">
              <label>Name (slug)</label>
              <input
                type="text"
                value={form.name}
                onChange={set('name')}
                placeholder="e.g. production"
                required
                disabled={isEdit}
              />
              <div className="te-form-hint">Unique identifier, cannot be changed after creation</div>
            </div>
            <div className="te-form-group">
              <label>Label</label>
              <input type="text" value={form.label} onChange={set('label')} placeholder="e.g. Production Server" required />
            </div>
          </div>

          <h3 className="te-section-title">PostgreSQL</h3>
          <div className="te-form-row">
            <div className="te-form-group te-form-group--wide">
              <label>Host</label>
              <input type="text" value={form.db_host} onChange={set('db_host')} placeholder="db.example.com" required />
            </div>
            <div className="te-form-group te-form-group--narrow">
              <label>Port</label>
              <input type="number" value={form.db_port} onChange={setNum('db_port')} required />
            </div>
          </div>
          <div className="te-form-row">
            <div className="te-form-group">
              <label>Database</label>
              <input type="text" value={form.db_name} onChange={set('db_name')} placeholder="cerebralmachines" />
            </div>
            <div className="te-form-group">
              <label>SSL Mode</label>
              <select value={form.db_sslmode} onChange={set('db_sslmode')}>
                {SSLMODE_OPTIONS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="te-form-row">
            <div className="te-form-group">
              <label>User</label>
              <input type="text" value={form.db_user} onChange={set('db_user')} placeholder="admin" />
            </div>
            <div className="te-form-group">
              <label>Password</label>
              <input
                type="password"
                value={form.db_pass}
                onChange={set('db_pass')}
                placeholder={isEdit ? '(unchanged if left blank)' : ''}
              />
            </div>
          </div>

          <h3 className="te-section-title">Qdrant</h3>
          <div className="te-form-row">
            <div className="te-form-group te-form-group--wide">
              <label>Host</label>
              <input type="text" value={form.qdrant_host} onChange={set('qdrant_host')} placeholder="qdrant.example.com" required />
            </div>
            <div className="te-form-group te-form-group--narrow">
              <label>gRPC Port</label>
              <input type="number" value={form.qdrant_grpc_port} onChange={setNum('qdrant_grpc_port')} required />
            </div>
          </div>

          <div className="te-form-actions">
            <button type="button" className="te-btn-cancel" onClick={onCancel}>Cancel</button>
            <button
              type="submit"
              className="te-btn-save"
              disabled={saving || !form.name || !form.label || !form.db_host || !form.qdrant_host}
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TransferEnvironmentsPage() {
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editEnv, setEditEnv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});

  const fetchEnvironments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.listTransferEnvironments();
      setEnvironments(data.environments || []);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load environments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEnvironments(); }, [fetchEnvironments]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editEnv && !payload.db_pass) {
        delete payload.db_pass;
      }
      if (editEnv) {
        await apiService.updateTransferEnvironment(editEnv.id, payload);
      } else {
        await apiService.createTransferEnvironment(payload);
      }
      setShowModal(false);
      setEditEnv(null);
      fetchEnvironments();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete environment "${name}"?`)) return;
    try {
      await apiService.deleteTransferEnvironment(id);
      fetchEnvironments();
    } catch (err) {
      alert(err?.message || 'Delete failed');
    }
  };

  const handleTest = async (id) => {
    setTestResults((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const result = await apiService.testTransferEnvironment(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [id]: { success: false, error: err?.message || 'Test failed' },
      }));
    }
  };

  const openEdit = (env) => {
    setEditEnv(env);
    setShowModal(true);
  };

  const openNew = () => {
    setEditEnv(null);
    setShowModal(true);
  };

  return (
    <div className="user-home-layout__page te-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">Transfer Environments</h1>
        <p className="user-home-layout__page-subtitle">
          Configure target environments for copying graphs between deployments.
        </p>
      </div>

      <div className="user-home-layout__card">
        <div className="te-toolbar">
          <h2 className="user-home-layout__section-title" style={{ margin: 0 }}>Configured Environments</h2>
          <button className="te-btn-add" onClick={openNew}>+ Add Environment</button>
        </div>

        {loading && <div className="te-loading">Loading environments...</div>}
        {error && <p style={{ color: '#ef4444' }}>{error}</p>}

        {!loading && !error && environments.length === 0 && (
          <div className="te-empty">
            <div style={{ fontSize: 40, marginBottom: 8 }}>{'{ }'}</div>
            <p>No transfer environments configured yet.</p>
            <p>Add your first environment to enable graph transfers.</p>
          </div>
        )}

        {!loading && environments.length > 0 && (
          <div className="te-grid">
            {environments.map((env) => {
              const tr = testResults[env.id];
              return (
                <div key={env.id} className="te-card">
                  <div className="te-card-icon">ENV</div>
                  <div className="te-card-info">
                    <div className="te-card-header">
                      <h3 className="te-card-name">{env.label}</h3>
                      <span className="te-card-badge">{env.name}</span>
                    </div>
                    <div className="te-card-meta">
                      <span>DB: <strong>{env.db_host}:{env.db_port}/{env.db_name}</strong></span>
                      <span>Qdrant: <strong>{env.qdrant_host}:{env.qdrant_grpc_port}</strong></span>
                      <span>User: {env.db_user}</span>
                      <span>Pass: {env.db_pass_masked || '(none)'}</span>
                    </div>
                    {tr && !tr.loading && (
                      <div className={`te-test-result ${tr.success ? 'te-test-result--success' : 'te-test-result--error'}`}>
                        {tr.success
                          ? `Connected (PG: ${tr.pg_latency_ms}ms, Qdrant: ${tr.qdrant_latency_ms}ms)`
                          : `${tr.pg_error ? `PG: ${tr.pg_error}` : ''}${tr.qdrant_error ? ` Qdrant: ${tr.qdrant_error}` : ''}${tr.error ? tr.error : ''}`
                        }
                      </div>
                    )}
                  </div>
                  <div className="te-card-actions">
                    <button className="te-btn-test" onClick={() => handleTest(env.id)} disabled={tr?.loading}>
                      {tr?.loading ? 'Testing...' : 'Test'}
                    </button>
                    <button onClick={() => openEdit(env)}>Edit</button>
                    <button className="te-btn-delete" onClick={() => handleDelete(env.id, env.name)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <EnvFormModal
          initial={editEnv ? { ...editEnv, db_pass: '' } : null}
          onSave={handleSave}
          onCancel={() => { setShowModal(false); setEditEnv(null); }}
          saving={saving}
        />
      )}
    </div>
  );
}

export default TransferEnvironmentsPage;
